/**
 * SHAREABLE ASSETS — one definition, four consumers (Node/CommonJS, browser-safe).
 *
 * A shareable asset is a link the AI SENDS to a lead, with the condition that should
 * surface it. It is the counterpart to the knowledge base: the KB is what the AI READS
 * to answer questions, this is what the AI HANDS OVER.
 *
 * This file exists so there is exactly ONE mechanism. Before it, the normaliser lived
 * privately inside surfAgent.js, so the Knowledge UI would have needed its own copy of
 * the rules, and the two would have drifted the first time either changed. Consumers:
 *   - src/api_routes/surfAgent.js        (Surf captures an asset in conversation)
 *   - src/api_routes/knowledgeBaseRoutes (the AI Studio "send this" answer)
 *   - src/components/settings/Knowledgebase.jsx (asks discuss-or-send on paste)
 *   - workers/scrapeQuality.js           (a file-share URL cannot be scraped usefully)
 *
 * No CommonJS-only syntax beyond module.exports, so the bundler can pull it into the
 * React app unchanged.
 */

// Hosts that serve a FILE, or a FOLDER of files, rather than a readable page. Pasting
// one of these into "add a website" can only ever scrape chrome: a file listing, or a
// login form. A Drive folder of photos is not a page that scraped badly, it is not a
// page at all, and no retry changes that.
const FILE_SHARE_HOSTS = [
  'drive.google.com', 'docs.google.com', 'dropbox.com', 'box.com',
  'onedrive.live.com', '1drv.ms', 'sharepoint.com', 'wetransfer.com',
  'icloud.com', 'mega.nz',
];

const FILE_EXTENSIONS = /\.(pdf|jpe?g|png|gif|webp|zip|rar|xlsx?|docx?|pptx?|csv)(\?|#|$)/i;

/**
 * Is this URL a file or a file-share, rather than a page?
 *
 * Treat the answer as a HINT, never a gate. It decides whether we ASK the tenant what
 * they meant, and asking is cheap while guessing wrong is not. A tenant with a genuine
 * readable page on a listed host just answers "intel to discuss" and carries on.
 */
function isFileShareUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  if (!u) return false;
  if (FILE_EXTENSIONS.test(u)) return true;
  return FILE_SHARE_HOSTS.some((h) => u.includes(h));
}

/**
 * Normalise and validate assets before storage.
 *
 * `send_when` is MANDATORY and an entry without one is DROPPED, not stored with a
 * default. A stored asset with no trigger is precisely what produced Exodus's "I had to
 * push 3 times before it was shared" (2026-08-03): the link sat in their campaign
 * knowledge with nothing saying when to use it, so surfacing it was left to chance.
 * Storing an untriggered asset would recreate that exact failure while looking like the
 * feature worked.
 *
 * Deliberately NOT industry-aware: no keyword matching, no URL classification, no guess
 * about what the business does. The tenant's own words for the trigger go to the model,
 * which decides. A recruiter's "when they ask about the role" and a wholesaler's "when a
 * buyer asks for photos" are one feature.
 *
 * @returns {Array<{label: string, url: string, send_when: string}>}
 */
function normalizeShareableAssets(raw, { campaignName = '', tenantId = '', log = console } = {}) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const a of raw) {
    if (!a || typeof a !== 'object') continue;
    const label    = String(a.label || '').trim();
    const url      = String(a.url || '').trim();
    const sendWhen = String(a.send_when || '').trim();
    if (!url) continue;
    if (!/^https?:\/\//i.test(url)) {
      log.warn(`[shareableAssets] dropped, not an http(s) url (campaign="${campaignName}", tenant=${tenantId}): ${url.slice(0, 120)}`);
      continue;
    }
    if (!sendWhen) {
      log.warn(`[shareableAssets] dropped, no send_when (campaign="${campaignName}", tenant=${tenantId}, label="${label}") — an asset with no trigger is the "had to push 3 times" bug, so it is not stored`);
      continue;
    }
    out.push({ label: label || 'Shared link', url, send_when: sendWhen });
  }
  return out;
}

/**
 * Merge incoming assets into what a campaign already has.
 *
 * Additive by default. Same-URL entries are REPLACED in place, so re-stating a link
 * updates its trigger instead of storing it twice. `replaceAll` must come from an
 * explicit tenant instruction, never a tidy-up instinct.
 */
function mergeShareableAssets(existing, incoming, { replaceAll = false } = {}) {
  const current = Array.isArray(existing) ? existing : [];
  if (replaceAll) return incoming;
  const incomingUrls = new Set(incoming.map((a) => String(a.url || '').toLowerCase()));
  return [...current.filter((a) => !incomingUrls.has(String(a?.url || '').toLowerCase())), ...incoming];
}

module.exports = {
  FILE_SHARE_HOSTS,
  FILE_EXTENSIONS,
  isFileShareUrl,
  normalizeShareableAssets,
  mergeShareableAssets,
};
